import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import { sans, mono, green, red, amber, useTheme } from '../../tokens';
import { headcountHistory, departureHistory, departmentList } from '../../data/headcountHistory';
import { getCanonicalHeadcount, assertHeadcountConsistent } from '../../data/canonicalHeadcount';

// CFS-basis burn data (same as Dashboard / My Overview — single source of truth)
// Net = total cash decrease per CFS (operating + investing + financing)
const CFS_MONTHS = ['Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb'];
const CFS_MONTH_KEYS = ['2025-09', '2025-10', '2025-11', '2025-12', '2026-01', '2026-02'];
const CFS_BURN_NET = [375000, 668000, 1016000, 2640000, 1910000, 179000];
const CFS_INFLOWS = [0, 0, 0, 0, 134000, 853000];
const CFS_BURN_GROSS = CFS_BURN_NET.map((v, i) => v + CFS_INFLOWS[i]);

const BUDGET_MONTHLY = {
  '2025-08': 950000, '2025-09': 980000, '2025-10': 1050000,
  '2025-11': 1100000, '2025-12': 1150000, '2026-01': 1200000,
  '2026-02': 1250000,
};

function DeptCheckPicker({ t, stackDepts, selected, onToggle, colors }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const handleDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleDoc);
    return () => document.removeEventListener('mousedown', handleDoc);
  }, [open]);

  const options = [{ key: 'all', label: 'All Company' }, ...stackDepts.map(d => ({ key: d, label: d }))];
  const selectedOptions = selected.map(key => options.find(o => o.key === key)).filter(Boolean);

  return (
    <div ref={ref} onClick={e => e.stopPropagation()} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
      {/* Selected chips displayed before the picker */}
      {selectedOptions.map((opt, i) => {
        const color = colors[i % colors.length];
        return (
          <span key={opt.key} style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            padding: '2px 8px', borderRadius: 10,
            background: color + '18', color,
            fontSize: 9, fontWeight: 600, fontFamily: sans,
          }}>
            {opt.label}
            {opt.key !== 'all' && (
              <button onClick={() => onToggle(opt.key)} style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color, padding: 0, marginLeft: 2, lineHeight: 1, fontSize: 11,
              }}>×</button>
            )}
          </span>
        );
      })}

      {/* Dropdown trigger */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          padding: '3px 10px', borderRadius: 10,
          border: `1px dashed ${t.border}`, background: t.bgCard,
          color: t.textMuted, fontSize: 9, fontWeight: 600, fontFamily: sans,
          cursor: 'pointer',
        }}
      >
        + dept
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: 4,
          background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          zIndex: 100, minWidth: 220, maxHeight: 280, overflowY: 'auto', padding: 4,
        }}>
          {options.map(opt => {
            const checked = selected.includes(opt.key);
            return (
              <label key={opt.key} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '6px 10px', borderRadius: 4, cursor: 'pointer',
                fontSize: 11, fontFamily: sans, color: t.textPrimary,
              }}
                onMouseEnter={e => e.currentTarget.style.background = t.bgSurface}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggle(opt.key)}
                  style={{ cursor: 'pointer' }}
                />
                {opt.label}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function KPISmallMultiples({ totalLiquidity }) {
  const t = useTheme();
  const months = CFS_MONTHS;
  const monthLabelsRaw = CFS_MONTHS;

  const hcLookup = useMemo(() => {
    const map = {};
    headcountHistory.forEach(h => { map[h.month] = h.total; });
    return map;
  }, []);

  const burnData = CFS_BURN_NET;
  const hcData = CFS_MONTH_KEYS.map(m => hcLookup[m] || null);

  // EOM balances from CFS (same as Dashboard)
  const actualCash = [43850000, 42840000, 40180000, 37400000, 35490000, 35290000];

  const budgetCash = useMemo(() => {
    const arr = new Array(months.length);
    arr[0] = actualCash[0];
    for (let i = 1; i < months.length; i++) {
      arr[i] = arr[i - 1] - (BUDGET_MONTHLY[CFS_MONTH_KEYS[i - 1]] || 1200000);
    }
    return arr;
  }, [months]);

  const monthLabels = CFS_MONTHS;

  function currentValue(data) {
    for (let i = data.length - 1; i >= 0; i--) {
      if (data[i] != null) return data[i];
    }
    return 0;
  }

  const variance = (actualCash[actualCash.length - 1] || 0) - (budgetCash[budgetCash.length - 1] || 0);
  const isOverBudget = variance < 0;

  const grossBurnData = CFS_BURN_GROSS;

  // 6-month averages
  const burnAvg6mo = useMemo(() => {
    const last6 = burnData.slice(-6).filter(v => v != null);
    return last6.length > 0 ? Math.round(last6.reduce((s, v) => s + v, 0) / last6.length) : 0;
  }, [burnData]);

  const grossAvg6mo = useMemo(() => {
    const last6 = grossBurnData.slice(-6).filter(v => v != null);
    return last6.length > 0 ? Math.round(last6.reduce((s, v) => s + v, 0) / last6.length) : 0;
  }, [grossBurnData]);

  // ── Burn toggle state ────────────────────────────────────────────
  const BURN_NET_COLOR = '#EF4444';
  const BURN_GROSS_COLOR = '#F97316';
  const [showNet, setShowNet] = useState(true);
  const [showGross, setShowGross] = useState(false);

  const toggleNet = () => { if (!showNet || showGross) setShowNet(!showNet); };
  const toggleGross = () => { if (!showGross || showNet) setShowGross(!showGross); };

  function makeBurnChart() {
    const datasets = [];
    if (showNet) {
      datasets.push({
        label: 'Net Burn',
        data: burnData,
        borderColor: BURN_NET_COLOR,
        backgroundColor: showGross ? 'transparent' : BURN_NET_COLOR + '18',
        fill: !showGross,
        tension: 0.3, pointRadius: 4, pointBackgroundColor: BURN_NET_COLOR,
        pointBorderColor: '#fff', pointBorderWidth: 1.5, borderWidth: 2,
      });
    }
    if (showGross) {
      datasets.push({
        label: 'Gross Burn',
        data: grossBurnData,
        borderColor: BURN_GROSS_COLOR,
        backgroundColor: BURN_GROSS_COLOR + '12',
        fill: true,
        tension: 0.3, pointRadius: 4, pointBackgroundColor: BURN_GROSS_COLOR,
        pointBorderColor: '#fff', pointBorderWidth: 1.5, borderWidth: 2,
        borderDash: showNet ? [5, 3] : [],
      });
    }
    // Average line
    const avgVal = showNet ? burnAvg6mo : grossAvg6mo;
    const avgColor = showNet ? BURN_NET_COLOR : BURN_GROSS_COLOR;
    datasets.push({
      label: '6-mo avg',
      data: monthLabels.map(() => avgVal),
      borderColor: avgColor + '50', borderWidth: 1, borderDash: [3, 4],
      pointRadius: 0, fill: false, tension: 0,
    });
    return { labels: monthLabels, datasets };
  }

  const fmtBurn = v => v >= 1000000 ? '$' + (v / 1000000).toFixed(1) + 'M' : '$' + (v / 1000).toFixed(0) + 'K';

  // ── Headcount chart ─────────────────────────────────────────────
  function makeHcChart() {
    return {
      labels: monthLabels,
      datasets: [{
        label: 'Headcount',
        data: hcData,
        borderColor: '#3B82F6',
        backgroundColor: '#3B82F6' + '18',
        fill: true, tension: 0.3, pointRadius: 4,
        pointBackgroundColor: '#3B82F6', pointBorderColor: '#fff',
        pointBorderWidth: 1.5, pointHitRadius: 8, borderWidth: 2,
      }],
    };
  }

  // ── Chart.js animation: only on first paint, disabled after ──────
  const [hasAnimated, setHasAnimated] = useState(false);
  useEffect(() => {
    const t2 = setTimeout(() => setHasAnimated(true), 1200);
    return () => clearTimeout(t2);
  }, []);

  const chartAnimation = hasAnimated ? false : {
    duration: 800,
    easing: 'easeOutQuart',
  };

  // ── Headcount: full chart (expanded) ────────────────────────────
  // hcDepts is an array of department keys. `['all']` (default) shows the
  // total; picking one or more specific department keys adds a line per
  // department so investors can compare them side-by-side.
  const [hcDepts, setHcDepts] = useState(['all']);
  const hcDept = hcDepts[0] || 'all'; // legacy single-select used in a few spots
  const toggleDept = (key) => {
    setHcDepts(prev => {
      if (key === 'all') return ['all'];
      const without = prev.filter(d => d !== 'all');
      if (without.includes(key)) {
        const next = without.filter(d => d !== key);
        return next.length ? next : ['all'];
      }
      return [...without, key];
    });
  };
  const HC_LINE_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EC4899', '#8B5CF6', '#EF4444', '#06B6D4', '#84CC16'];
  const HC_COLOR = '#3B82F6';
  const stackDepts = departmentList.filter(d => d !== 'Demo Medical' && d !== 'Mechanical R&D');

  const hcFullData = useMemo(() => {
    const hcNow = new Date();
    const curMonth = `${hcNow.getFullYear()}-${String(hcNow.getMonth() + 1).padStart(2, '0')}`;
    const extHistory = [...headcountHistory];
    const lastEntry = extHistory[extHistory.length - 1];
    if (lastEntry && lastEntry.month < curMonth) {
      extHistory.push({ ...lastEntry, month: curMonth });
    }
    const hcCutoff = `${hcNow.getFullYear() - 1}-${String(hcNow.getMonth() + 1).padStart(2, '0')}`;
    return extHistory.filter(h => h.month >= hcCutoff);
  }, []);

  // Use the raw total (matches canonical source) so the headline number
  // stays identical whether the chart is collapsed or expanded.
  const getHcValFor = (h, dept) => {
    if (dept === 'all') return h.total;
    if (dept === 'Mechanical Design') return (h.departments['Mechanical Design'] || 0) + (h.departments['Mechanical R&D'] || 0);
    return h.departments[dept] || 0;
  };
  const getHcVal = (h) => getHcValFor(h, hcDept);

  const hcCurrent = hcFullData.length > 0 ? getHcVal(hcFullData[hcFullData.length - 1]) : 0;
  const hcGrowth = useMemo(() => {
    if (hcFullData.length < 2) return 0;
    const past = getHcVal(hcFullData[0]);
    return past > 0 ? Math.round(((hcCurrent - past) / past) * 100) : 0;
  }, [hcFullData, hcDept, hcCurrent]);

  const depByMonth = useMemo(() => {
    const map = {};
    departureHistory.forEach(d => {
      const filtered = hcDept === 'all' ? d.people : d.people.filter(p => p.dept === hcDept);
      if (filtered.length > 0) map[d.month] = { count: filtered.length, people: filtered };
    });
    return map;
  }, [hcDept]);

  function makeHcFullChart() {
    const labels = hcFullData.map(h => {
      const [y, mo] = h.month.split('-');
      const mn = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][parseInt(mo) - 1];
      return mo === '01' || h === hcFullData[0] ? `${mn} '${y.slice(2)}` : mn;
    });
    // One dataset per selected department. With 'all' we keep the filled
    // area style; when comparing multiple depts we drop the fill so
    // overlapping lines stay readable.
    const multi = hcDepts.length > 1 || (hcDepts[0] && hcDepts[0] !== 'all');
    const datasets = hcDepts.map((d, idx) => {
      const color = HC_LINE_COLORS[idx % HC_LINE_COLORS.length];
      return {
        label: d === 'all' ? 'Headcount' : d,
        data: hcFullData.map(h => getHcValFor(h, d)),
        type: 'line',
        borderColor: color,
        backgroundColor: color + (multi ? '00' : '20'),
        fill: !multi, tension: 0.3, pointRadius: 3,
        pointBackgroundColor: color, borderWidth: 2,
      };
    });
    const hcVals = hcFullData.map(h => getHcValFor(h, hcDept));
    return { labels, datasets, hcVals };
  }

  // Fixed Y max based on total company headcount (doesn't change with dept filter)
  const hcYMax = useMemo(() => {
    const allTotals = hcFullData.map(h => h.total);
    return Math.ceil((Math.max(...allTotals) + 5) / 5) * 5; // round up to nearest 5
  }, [hcFullData]);

  // Canonical headcount — used for both collapsed and expanded headline numbers
  // so the two displays never diverge.
  const canonicalHC = getCanonicalHeadcount();
  assertHeadcountConsistent(canonicalHC.total, 'KPISmallMultiples');

  const hcFullOptions = useMemo(() => ({
    responsive: true, maintainAspectRatio: false,
    animation: chartAnimation,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: ctx => `${ctx.dataset.label}: ${ctx.parsed.y}`,
          afterLabel: ctx => {
            const hcVals = hcFullData.map(getHcVal);
            const idx = ctx.dataIndex;
            const mo = hcFullData[idx].month;
            const dep = depByMonth[mo];
            const cur = hcVals[idx];
            const prev = idx > 0 ? hcVals[idx - 1] : 0;
            const depCount = dep ? dep.count : 0;
            const arrivals = cur - prev + depCount;
            const lines = [];
            if (arrivals > 0) lines.push(`Arrivals: +${arrivals}`);
            if (depCount > 0) lines.push(`Departures: -${depCount}`);
            return lines.join('\n');
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: hcYMax,
        ticks: { color: t.textMuted, font: { size: 8, family: mono } },
        grid: { color: t.borderLight },
      },
      x: {
        ticks: { color: t.textMuted, font: { size: 8, family: sans }, maxRotation: 0 },
        grid: { display: false },
      },
    },
  }), [hcDept, chartAnimation, depByMonth, hcYMax]);

  // ── Runway projection (based on last approved forecast/budget) ────
  const RUNWAY_COLOR = '#D4A843'; // yellow butter
  const latestCash = actualCash[actualCash.length - 1] || totalLiquidity || 35290000;
  const budgetBurnFwd = 1250000; // last approved monthly forecast burn
  const runwayProjection = useMemo(() => {
    const projMonths = [];
    const projBalances = [];
    let bal = latestCash;
    const startDate = new Date(2026, 1); // Feb 2026 = last known
    const moNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    for (let i = 1; i <= 36 && bal > 0; i++) {
      const d = new Date(startDate);
      d.setMonth(d.getMonth() + i);
      bal -= budgetBurnFwd;
      projMonths.push(moNames[d.getMonth()] + (d.getFullYear() !== 2026 ? " '" + String(d.getFullYear()).slice(2) : ''));
      projBalances.push(Math.max(0, bal));
    }
    return { labels: projMonths, data: projBalances };
  }, [latestCash]);

  const runwayMonths = Math.ceil(latestCash / budgetBurnFwd);
  const runwayDate = (() => {
    const d = new Date(2026, 1);
    d.setMonth(d.getMonth() + runwayMonths);
    const q = Math.ceil((d.getMonth() + 1) / 3);
    return `Q${q} ${d.getFullYear()}`;
  })();
  const runwayColor = RUNWAY_COLOR;

  const runwayChartData = {
    labels: runwayProjection.labels,
    datasets: [{
      label: 'Projected Cash',
      data: runwayProjection.data,
      borderColor: runwayColor,
      backgroundColor: runwayColor + '12',
      fill: true,
      tension: 0.3,
      pointRadius: runwayProjection.data.map((_, i) => (i + 1) % 3 === 0 ? 4 : 0),
      pointBackgroundColor: runwayColor,
      pointBorderColor: '#fff',
      pointBorderWidth: 1.5,
      pointHitRadius: 6,
      borderWidth: 2,
    }],
  };

  const runwayChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: chartAnimation,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: $${(ctx.parsed.y / 1000000).toFixed(1)}M`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { font: { size: 8, family: mono }, color: t.textMuted, maxTicksLimit: 5, callback: v => '$' + (v / 1000000).toFixed(0) + 'M' },
        grid: { color: t.borderLight },
        border: { display: false },
      },
      x: {
        ticks: { font: { size: 7, family: sans }, color: t.textMuted, maxRotation: 0, maxTicksLimit: 8 },
        grid: { display: false },
        border: { display: false },
      },
    },
  };

  const smallChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { enabled: true } },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { font: { size: 8, family: mono }, color: t.textMuted, maxTicksLimit: 5 },
        grid: { color: t.borderLight },
        border: { display: false },
      },
      x: {
        ticks: { font: { size: 9, family: sans }, color: t.textMuted, maxRotation: 0 },
        grid: { display: false },
        border: { display: false },
      },
    },
  };

  // ── Large chart: Cash Balance + Budget ───────────────────────────
  const cashChartData = {
    labels: monthLabels,
    datasets: [
      {
        label: 'Actual Cash',
        data: actualCash,
        borderColor: green,
        backgroundColor: green + '15',
        fill: true,
        tension: 0.3,
        pointRadius: 4,
        pointBackgroundColor: green,
        pointBorderColor: '#fff',
        pointBorderWidth: 1.5,
        pointHitRadius: 8,
        borderWidth: 2.5,
      },
      {
        label: 'Budget',
        data: budgetCash,
        borderColor: t.textMuted,
        backgroundColor: 'transparent',
        fill: false,
        tension: 0.3,
        pointRadius: 3,
        pointBackgroundColor: t.textMuted,
        pointBorderColor: '#fff',
        pointBorderWidth: 1,
        borderWidth: 1.5,
        borderDash: [5, 3],
      },
    ],
  };

  const cashChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: $${(ctx.parsed.y / 1000000).toFixed(1)}M`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { font: { size: 9, family: mono }, color: t.textMuted, maxTicksLimit: 6, callback: v => '$' + (v / 1000000).toFixed(0) + 'M' },
        grid: { color: t.borderLight },
        border: { display: false },
      },
      x: {
        ticks: { font: { size: 9, family: sans }, color: t.textMuted, maxRotation: 0 },
        grid: { display: false },
        border: { display: false },
      },
    },
  };

  // ── Entrance animation state ─────────────────────────────────────
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // Click-to-expand state for the 4 small-multiples cards
  const [expandedChart, setExpandedChart] = useState(null);

  const CARD_KEYS = ['cash', 'burn', 'headcount', 'runway'];
  const isExpanded = (key) => expandedChart === key;
  const isHidden = (key) => expandedChart !== null && expandedChart !== key;
  const toggleExpand = (key) => setExpandedChart(prev => prev === key ? null : key);

  // Track whether initial entrance is done so stagger only applies once
  const [entranceDone, setEntranceDone] = useState(false);
  useEffect(() => {
    if (mounted) {
      const t2 = setTimeout(() => setEntranceDone(true), 600);
      return () => clearTimeout(t2);
    }
  }, [mounted]);

  // Each card's chart color — keep these in sync with the chart definitions
  // below. Expanded-card border matches the chart line instead of always green.
  const CARD_COLORS = {
    cash: green,
    burn: '#EF4444',     // matches BURN_NET_COLOR
    headcount: '#3B82F6', // matches HC_COLOR
    runway: '#D4A843',    // matches RUNWAY_COLOR
  };

  const cardStyle = (key) => {
    const idx = CARD_KEYS.indexOf(key);
    // Stagger only on first entrance
    const delay = (!entranceDone && mounted) ? idx * 150 : 0;
    const accent = CARD_COLORS[key] || green;
    return {
      background: t.bgCard,
      border: `1px solid ${isExpanded(key) ? accent : t.border}`,
      borderRadius: 8,
      padding: 14,
      cursor: 'pointer',
      overflow: 'hidden',
      minWidth: 0,
      opacity: mounted ? 1 : 0,
      transform: mounted ? 'translateY(0)' : 'translateY(10px)',
      transition: `opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${delay}ms, transform 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${delay}ms, border-color 0.2s ease`,
    };
  };
  const chartHeight = (key) => isExpanded(key) ? 280 : 140;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
      {/* ── Cash vs Budget ─────────────────────────────────────────── */}
      <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 8, padding: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: t.textMuted, fontFamily: sans, fontWeight: 600 }}>Cash vs Budget</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <div style={{ width: 8, height: 2, background: green, borderRadius: 1 }} />
              <span style={{ fontSize: 7, fontFamily: sans, color: t.textMuted }}>Actual</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <div style={{ width: 8, height: 0, borderTop: `1px dashed ${t.textMuted}` }} />
              <span style={{ fontSize: 7, fontFamily: sans, color: t.textMuted }}>Budget</span>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 8 }}>
          <span style={{ fontSize: 18, fontWeight: 700, fontFamily: mono, color: green }}>${(currentValue(actualCash) / 1000000).toFixed(1)}M</span>
          <span style={{ fontSize: 9, fontWeight: 600, fontFamily: mono, color: isOverBudget ? red : green }}>
            {isOverBudget ? '' : '+'}${(Math.abs(variance) / 1000000).toFixed(1)}M
          </span>
        </div>
        <div style={{ height: 140 }}>
          <Line data={cashChartData} options={cashChartOptions} />
        </div>
      </div>
        {/* Burn chart with Gross/Net toggle */}
        <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 8, padding: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: t.textMuted, fontFamily: sans, fontWeight: 600 }}>
              Monthly Burn
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              <button onClick={toggleNet} style={{
                padding: '2px 10px', borderRadius: 10, fontSize: 9, fontFamily: sans,
                border: `1px solid ${showNet ? BURN_NET_COLOR : t.border}`,
                background: showNet ? BURN_NET_COLOR + '18' : 'transparent',
                color: showNet ? BURN_NET_COLOR : t.textMuted,
                fontWeight: showNet ? 600 : 400, cursor: 'pointer',
              }}>Net</button>
              <button onClick={toggleGross} style={{
                padding: '2px 10px', borderRadius: 10, fontSize: 9, fontFamily: sans,
                border: `1px solid ${showGross ? BURN_GROSS_COLOR : t.border}`,
                background: showGross ? BURN_GROSS_COLOR + '18' : 'transparent',
                color: showGross ? BURN_GROSS_COLOR : t.textMuted,
                fontWeight: showGross ? 600 : 400, cursor: 'pointer',
              }}>Gross</button>
            </div>
            {isExpanded('headcount') && (
              <DeptCheckPicker
                t={t}
                stackDepts={stackDepts}
                selected={hcDepts}
                onToggle={toggleDept}
                colors={HC_LINE_COLORS}
              />
            )}
          </div>
          {isExpanded('headcount') ? (
            <div style={{ textAlign: 'right' }}>
              <span style={{ fontSize: 22, fontWeight: 700, fontFamily: mono, color: t.textPrimary }}>
                {hcDepts.length === 1 && hcDepts[0] === 'all' ? canonicalHC.total : (hcDepts.length === 1 ? hcCurrent : `${hcDepts.length} depts`)}
              </span>
              <span style={{ fontSize: 10, color: t.textMuted, fontFamily: sans, marginLeft: 6 }}>
                {hcDepts.length > 1 ? 'selected' : 'employees'}
              </span>
              <div style={{ fontSize: 9, fontFamily: mono, color: t.textMuted, marginTop: 2 }}>
                <span style={{ color: t.textSecondary, fontWeight: 600 }}>{canonicalHC.men}</span> men
                <span style={{ color: t.textFaint, margin: '0 4px' }}>·</span>
                <span style={{ color: t.textSecondary, fontWeight: 600 }}>{canonicalHC.women}</span> women
              </div>
              <div style={{ fontSize: 10, fontFamily: mono, color: hcGrowth >= 0 ? green : red, marginTop: 2 }}>
                {hcGrowth >= 0 ? '+' : ''}{hcGrowth}% <span style={{ fontSize: 8, color: t.textMuted, fontFamily: sans }}>vs 12mo ago</span>
              </div>
            </div>
          ) : null}
        </div>

        {/* Headcount chart */}
        <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 8, padding: 14 }}>
          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: t.textMuted, fontFamily: sans, fontWeight: 600, marginBottom: 4 }}>
            Headcount
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, fontFamily: mono, color: '#3B82F6', marginBottom: 8 }}>
            {currentValue(hcData)}
          </div>
          <div style={{ height: 140 }}>
            <Line data={makeHcChart()} options={smallChartOptions} />
          </div>
        </div>
    </div>
  );
}
